// Version: 1.9
// v1.9: "STG Edge Node" in title case on the tag and tooltip.
// v1.8: The STG Edge Node's host carries an "STG Edge Node" tag (edge_node from app.py v4.39).
// v1.7: Number tiles use the shared .stat-card; inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v1.6: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v1.5: Known vulnerabilities on edge-discovered devices (CV-29): CVE badge next
//       to the IP, a red dot on affected services and an expandable list.
//       Admin-only — the API sends CVE data to STG admins only.
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { authFetch, useAuth } from './auth';
import { HostCveBadge, HostCveList, ServiceCveDot } from './NetworkCves';

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

const SERVICE_COLORS = {
  'Web':                '#00d4ff',
  'Secure Web':         '#00d4ff',
  'Remote Access':      '#ff2d55',
  'Remote Desktop':     '#ff2d55',
  'File Sharing':       '#e6b800',
  'File Transfer':      '#e6b800',
  'Secure File Transfer': '#e6b800',
  'Email':              '#30d158',
  'Secure Email':       '#30d158',
  'DNS':                '#86868b',
  'Network Services':   '#86868b',
  'Network Management': '#86868b',
  'Network Discovery':  '#86868b',
  'Printing':           '#86868b',
  'Database':           '#ff9f0a',
  'Directory Services': '#bf5af2',
  'Authentication':     '#bf5af2',
  'Windows Services':   '#86868b',
  'Network Service':    '#86868b',
};

function ServiceTag({ label, port, service, version, isAdmin, cveSource }) {
  const color = SERVICE_COLORS[label] || '#86868b';
  return (
    <span
      title={isAdmin ? `Port ${port} · ${service}${version ? ` ${version}` : ''}` : label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        background: `${color}18`,
        border: `1px solid ${color}40`,
        color,
        fontFamily: 'var(--font-mono)',
        whiteSpace: 'nowrap',
        cursor: isAdmin ? 'help' : 'default',
      }}
    >
      {isAdmin && port && (
        <span style={{ opacity: 0.6, fontSize: 10 }}>{port}</span>
      )}
      {label}
      {isAdmin && <ServiceCveDot service={cveSource} />}
    </span>
  );
}

function HostRow({ host, isAdmin, isNew, clientName }) {
  const [expanded, setExpanded] = useState(false);
  const services = host.services || [];

  // Group duplicate labels for client view
  const uniqueServices = isAdmin
    ? services
    : services.filter((s, i, arr) => arr.findIndex(x => x.label === s.label) === i);

  return (
    <tr
      onClick={() => setExpanded(e => !e)}
      style={{
        cursor: 'pointer',
        background: expanded ? 'rgba(0,212,255,0.04)' : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      {clientName != null && (
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{clientName}</span>
        </td>
      )}
      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isNew && (
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '2px 5px',
              borderRadius: 3, background: '#e6b80020',
              border: '1px solid #e6b80060', color: '#e6b800',
              fontFamily: 'var(--font-mono)', letterSpacing: '0.06em',
            }}>NEW</span>
          )}
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 13,
            color: 'var(--text)', fontWeight: 500,
          }}>
            {host.ip}
          </span>
          {isAdmin && <HostCveBadge host={host} />}
          {host.edge_node && <span className="tag tag-blue" title={`${host.edge_node.label}: the Edge Node`}>STG Edge Node</span>}
        </div>
      </td>
      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
        <span style={{ fontSize: 13, color: host.hostname ? 'var(--text)' : 'var(--text-muted)' }}>
          {host.hostname || '—'}
        </span>
        {isAdmin && host.os && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{host.os}</div>
        )}
      </td>
      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {uniqueServices.length > 0 ? uniqueServices.map((s, i) => (
            <ServiceTag
              key={i}
              label={s.label}
              port={s.port}
              service={s.service}
              version={s.version}
              isAdmin={isAdmin}
              cveSource={s}
            />
          )) : (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No services detected</span>
          )}
        </div>
        {isAdmin && <HostCveList host={host} />}
      </td>
    </tr>
  );
}

function HostCard({ host, isAdmin, isNew, clientName }) {
  const services = host.services || [];
  const uniqueServices = isAdmin
    ? services
    : services.filter((s, i, arr) => arr.findIndex(x => x.label === s.label) === i);

  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        {isNew && (
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '2px 5px',
            borderRadius: 3, background: '#e6b80020',
            border: '1px solid #e6b80060', color: '#e6b800',
            fontFamily: 'var(--font-mono)', letterSpacing: '0.06em',
          }}>NEW</span>
        )}
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 13,
          color: 'var(--text)', fontWeight: 600,
        }}>
          {host.ip}
        </span>
        {isAdmin && <HostCveBadge host={host} />}
        {host.edge_node && <span className="tag tag-blue" title={`${host.edge_node.label}: the Edge Node`}>STG Edge Node</span>}
        {clientName != null && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{clientName}</span>
        )}
      </div>
      <div style={{ marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: host.hostname ? 'var(--text)' : 'var(--text-muted)' }}>
          {host.hostname || '—'}
        </span>
        {isAdmin && host.os && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{host.os}</div>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {uniqueServices.length > 0 ? uniqueServices.map((s, i) => (
          <ServiceTag
            key={i}
            label={s.label}
            port={s.port}
            service={s.service}
            version={s.version}
            isAdmin={isAdmin}
            cveSource={s}
          />
        )) : (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No services detected</span>
        )}
      </div>
      {isAdmin && <HostCveList host={host} />}
    </div>
  );
}

export default function NetworkDevices() {
  const isMobile = useIsMobile();
  const { slug: urlSlug } = useParams();
  const { user }          = useAuth();
  const isAdmin           = user?.role === 'admin' || user?.role === 'manager';
  const slug              = urlSlug || user?.client_slug || '';
  // Aggregate (cross-client) mode: admin/manager browsing with no specific
  // client slug — e.g. the bare /network route from the main nav, mirroring
  // how Agent Nodes shows all devices filterable by client.
  const isAggregate = isAdmin && !slug;

  const [data, setData]         = useState(null);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [filter, setFilter]     = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [clientFilter, setClientFilter]   = useState('');
  const [showNewOnly, setShowNewOnly]     = useState(false);

  useEffect(() => {
    if (!slug && !isAggregate) return;
    setLoading(true);
    setError('');
    if (isAggregate) {
      authFetch('/api/admin/network-devices')
        .then(r => r.json())
        .then(d => {
          setClients(d.clients || []);
          setData({
            available: !!d.available,
            hosts: d.hosts || [],
            summary: { total_hosts: (d.hosts || []).length },
          });
          setLoading(false);
        })
        .catch(() => { setError('Failed to load network data'); setLoading(false); });
    } else {
      authFetch(`/api/clients/${slug}/network`)
        .then(r => r.json())
        .then(d => { setData(d); setLoading(false); })
        .catch(() => { setError('Failed to load network data'); setLoading(false); });
    }
  }, [slug, isAggregate]);

  const hosts      = data?.hosts || [];
  const summary    = data?.summary || {};
  const scannedAt  = summary.scanned_at
    ? new Date(summary.scanned_at).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  // All unique service labels across all hosts
  const allServiceLabels = useMemo(() => {
    const labels = new Set();
    hosts.forEach(h => (h.services || []).forEach(s => labels.add(s.label)));
    return ['all', ...Array.from(labels).sort()];
  }, [hosts]);

  // Filtered hosts
  const filteredHosts = useMemo(() => {
    return hosts.filter(h => {
      const q = filter.toLowerCase();
      const matchText = !filter || (
        h.ip.includes(filter) ||
        (h.hostname || '').toLowerCase().includes(q) ||
        (h.os || '').toLowerCase().includes(q) ||
        (h.label || '').toLowerCase().includes(q) ||
        (h.vendor || '').toLowerCase().includes(q) ||
        (h.services || []).some(s => (s.label || '').toLowerCase().includes(q))
      );
      const matchService = serviceFilter === 'all' ||
        (h.services || []).some(s => s.label === serviceFilter);
      const matchClient = !isAggregate || !clientFilter || h.client_slug === clientFilter;
      return matchText && matchService && matchClient && (!showNewOnly || h.is_new);
    });
  }, [hosts, filter, serviceFilter, clientFilter, isAggregate, showNewOnly]);

  const newCount = hosts.filter(h => h.is_new).length;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Network Devices</div>
        <div className="page-subtitle">
          {isAggregate
            ? 'Devices discovered across all clients'
            : 'Devices discovered on your network'}
          {!isAggregate && scannedAt && ` · Last scan ${scannedAt}`}
        </div>
      </div>

      {/* Summary stats */}
      {data?.available && (
        <div className="stat-tiles">
          {[
            { label: 'Total devices',  value: summary.total_hosts ?? 0, tone: 'accent', clickable: false },
            { label: 'New since last', value: newCount, tone: newCount > 0 ? 'amber' : 'muted', clickable: newCount > 0 },
            { label: 'With services',  value: hosts.filter(h => h.services?.length > 0).length, tone: '', clickable: false },
            isAggregate
              ? { label: 'Clients', value: clients.length, tone: 'muted', clickable: false }
              : { label: 'Subnet', value: summary.subnet || '—', tone: 'muted', clickable: false },
          ].map(({ label, value, tone, clickable }) => {
            const isActive = clickable && showNewOnly;
            const inner = (
              <>
                <div className={`stat-value ${tone}`}>{value}</div>
                <div className="stat-label">{isActive ? 'New only · clear' : label}</div>
              </>
            );
            return clickable
              ? <button key={label} type="button" className="stat-card stat-click" aria-pressed={isActive} onClick={() => setShowNewOnly(v => !v)}>{inner}</button>
              : <div key={label} className="stat-card">{inner}</div>;
          })}
        </div>
      )}

      {/* Filters */}
      {data?.available && hosts.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="input-sm"
            type="text"
            placeholder="Filter by IP, hostname, label or service…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ width: 240 }}
          />
          {isAggregate && (
            <select className="input-sm"
              value={clientFilter}
              onChange={e => setClientFilter(e.target.value)}>
              <option value="">All clients</option>
              {clients.map(c => (
                <option key={c.slug} value={c.slug}>{c.display_name || c.slug}</option>
              ))}
            </select>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {allServiceLabels.map(label => (
              <button className="btn btn-secondary btn-sm" aria-pressed={serviceFilter === label} style={{ textTransform: label === 'all' ? 'capitalize' : 'none' }}
                key={label}
                onClick={() => setServiceFilter(label)}
               
              >
                {label === 'all' ? 'All' : label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}><span className="spinner" /></div>
      ) : error ? (
        <div className="error-msg">{error}</div>
      ) : !data?.available ? (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
          {isAggregate
            ? 'No network scan data available across any client yet.'
            : 'No network scan data yet. Scans run automatically via Clearview Edge.'}
        </div>
      ) : filteredHosts.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
          No devices match your filter.
        </div>
      ) : isMobile ? (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
            {filteredHosts.map(host => (
              <HostCard
                key={isAggregate ? `${host.client_slug}-${host.ip}` : host.ip}
                host={host}
                isAdmin={isAdmin}
                isNew={host.is_new}
                clientName={isAggregate ? (clients.find(c => c.slug === host.client_slug)?.display_name || host.client_slug) : null}
              />
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
            {filteredHosts.length} of {hosts.length} device{hosts.length !== 1 ? 's' : ''}
          </div>
        </div>
      ) : (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {(isAggregate ? ['Client', 'IP Address', 'Hostname', 'Services'] : ['IP Address', 'Hostname', 'Services']).map(h => (
                  <th key={h} style={{
                    padding: '10px 16px', textAlign: 'left', fontSize: 10,
                    fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: 'var(--text-muted)', fontFamily: 'var(--font-mono)',
                    background: 'var(--card-bg)',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredHosts.map((host, i) => (
                <HostRow
                  key={isAggregate ? `${host.client_slug}-${host.ip}` : host.ip}
                  host={host}
                  isAdmin={isAdmin}
                  isNew={host.is_new}
                  clientName={isAggregate ? (clients.find(c => c.slug === host.client_slug)?.display_name || host.client_slug) : null}
                />
              ))}
            </tbody>
          </table>
          <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)' }}>
            {filteredHosts.length} of {hosts.length} device{hosts.length !== 1 ? 's' : ''}
            {isAdmin && <span style={{ marginLeft: 8, fontFamily: 'var(--font-mono)' }}>· Admin view: port numbers visible on hover</span>}
          </div>
        </div>
      )}
    </div>
  );
}